import { useForm, yupResolver } from "@mantine/form";
import React, { useState } from "react";
import { loginValidations } from "./loginValidations";
import { useNavigate } from "react-router-dom";
import { useAuth, useLogger, useToast } from "../../hooks";
import { encDec } from "../../utils";
import { LogLevel, ToastType } from "../../enums";
import { loginService } from "./loginService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faLock } from "@fortawesome/free-solid-svg-icons";
import AuthGovBar from "../../components/common/AuthBanner/AuthGovBar";
import AuthTopBar from "../../components/common/AuthBanner/AuthTopBar";
import AuthBanner from "../../components/common/AuthBanner/AuthBanner";
import AuthMobileHeader from "../../components/common/AuthBanner/AuthMobileHeader";
import AuthMobileFooter from "../../components/common/AuthBanner/AuthMobileFooter";
import NationalEmblem from "../../assets/emblem.svg";
import "./Login.css";

// Cross-portal link to the citizen/user app. Baked in at build time per
// environment via REACT_APP_CITIZEN_PORTAL_LOGIN_URL (see .env.example) -
// falls back to production if an environment's build doesn't set it.
const CITIZEN_PORTAL_LOGIN_URL =
  process.env.REACT_APP_CITIZEN_PORTAL_LOGIN_URL || "https://seniorcitizen.dosje.gov.in/login";

// Where each role lands after signing in, keyed by m_roles.role_id. A role
// listed here does not hold the "Dashboard" menu (menu_id 1, /dashboard), so
// the default landing page would render an empty screen for it.
//
// These are landing pages only - the access_control matrix is what actually
// grants or denies each page, both in the sidebar and, via requireMenuAccess,
// at the API. Adding a role here does not give it access to anything.
const ROLE_LANDING_PAGES: Record<number, string> = {
  12: "/organisation-dashboard", // Organisations
  6: "/sage-dashboard", // Department - SAGE application counts
};

const Login: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  // UI-only login-type tab (does not change the login flow)
  const [activeTab, setActiveTab] = useState<"citizen" | "officer">("officer");
  const { log } = useLogger();
  const { setUserTokenToContext, login } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const validationSchema = loginValidations.validateLoginWithPassword();

  const form = useForm({
    initialValues: {
      mobile_number: "",
      password: "",
    },
    validate: yupResolver(validationSchema),
  });

  const handleLogin = async (values: {
    mobile_number: string;
    password: string;
  }) => {
    try {
      const encryptedPassword = encDec.encrypt(values.password);
      const response = await loginService.loginWithPassword(
        values.mobile_number,
        encryptedPassword,
      );

      if (response.data && response.data.data) {
        const token = response.data.data.token;
        setUserTokenToContext(token);
        await login();
        showToast("Login Successful", "Success", ToastType.SUCCESS);

        // These roles have no access to the internal admin dashboard, so
        // landing them there shows an empty page they then have to navigate
        // away from. Send each straight to its own dashboard instead.
        let roleId: number | undefined;
        try {
          roleId = Number(
            JSON.parse(atob(String(token).split(".")[1] || "")).role_id,
          );
        } catch {
          // An unreadable token is not a reason to block a successful login -
          // fall through to the default landing page.
        }
        const landingPage =
          (roleId !== undefined && ROLE_LANDING_PAGES[roleId]) || "/dashboard";
        navigate(landingPage, { replace: true });
      }

      // log(LogLevel.INFO, "Login :: handleLogin", response);
    } catch (error: any) {
      log(LogLevel.ERROR, "Login :: handleLogin", error);
      const errorMessage =
        error?.response?.data?.errorMessage ||
        "Login failed. Please check your credentials and try again.";
      showToast(errorMessage, "Error", ToastType.ERROR);
    }
  };

  const handleLoginWithOtp = () => {
    navigate("/otp-login");
  };

  const handleForgotPassword = () => {
    navigate("/forget-password");
  };

  return (
    <>
      <div className="fixed inset-0 flex flex-col overflow-hidden bg-[#f9fafb]">
        <AuthGovBar />
        <AuthTopBar />
        <div className="grid  grid-cols-12 flex-1 min-h-0">
          <AuthBanner />
        <div className="bg-[#E5EFF9] md:bg-transparent col-span-12 md:col-span-5 h-full px-5 md:px-8 py-4 overflow-y-auto flex flex-col justify-center">
          <AuthMobileHeader />

          <div className="flex-1 w-full flex flex-col justify-center">
            <div className="flex flex-col items-center">
              <img
                src={NationalEmblem}
                width={62}
                height={100}
                alt="national-emblem"
                className="h-20 md:h-auto mb-2 md:mb-4"
              />
            </div>

            {/* Login-type tabs (UI only) */}
            <div className="w-full sm:w-3/4 mx-auto">
              <div className="flex bg-[#E5E7EB] rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setActiveTab("officer")}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    activeTab === "officer"
                      ? "bg-[#003366] text-white shadow"
                      : "bg-transparent text-gray-500"
                  }`}
                >
                  Officer / Admin
                </button>
                <button
                  type="button"
                  onClick={() => {
                    window.location.href = CITIZEN_PORTAL_LOGIN_URL;
                  }}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    activeTab === "citizen"
                      ? "bg-[#003366] text-white shadow"
                      : "bg-transparent text-gray-500"
                  }`}
                >
                  Citizen / Beneficiary
                </button>
              </div>
            </div>

            <form onSubmit={form.onSubmit(handleLogin)}>
              <div className="mt-3 md:mt-5 mb-2 flex flex-col justify-center items-center space-y-4 ">
                <div className="w-full sm:w-3/4">
                  <h1 className="text-[#374151]  font-semibold text-xl sm:text-2xl mb-3 md:mb-5">
                    Login to your account
                  </h1>
                  <input
                    aria-label="Mobile Number"
                    id="mobile_number"
                    maxLength={10}
                    type="text"
                    autoComplete="tel"
                    className="border border-[#D1D5DB]  py-3 px-4 text-sm w-full  rounded-lg outline-none"
                    {...form.getInputProps("mobile_number")}
                    placeholder="Mobile Number"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    onChange={(e) => {
                      const onlyNums = e.target.value.replace(/\D/g, "");
                      form.setFieldValue("mobile_number", onlyNums);
                    }}
                    onPaste={(e) => {
                      const pastedValue = e.clipboardData.getData("text");
                      if (!/^\d+$/.test(pastedValue)) {
                        e.preventDefault();
                      }
                    }}
                  />

                  {form.errors.mobile_number && (
                    <p className="text-red-600 font-[500] text-xs mt-1">
                      {form.errors.mobile_number}
                    </p>
                  )}
                </div>
                <div className="relative w-full sm:w-3/4">
                  <input
                    aria-label="Password"
                    id="password"
                    maxLength={25}
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    className="border border-[#D1D5DB] w-full py-3 px-4 text-sm rounded-lg outline-none"
                    {...form.getInputProps("password")}
                    placeholder="Password"
                  />
                  {showPassword ? (
                    <FontAwesomeIcon
                      icon={faEyeSlash}
                      className="absolute top-4 right-4 cursor-pointer"
                      color="#003366"
                      onClick={() => setShowPassword(!showPassword)}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faEye}
                      className="absolute top-4 right-4 cursor-pointer"
                      color="#003366"
                      onClick={() => setShowPassword(!showPassword)}
                    />
                  )}

                  {form.errors.password && (
                    <p className="text-red-600 font-[500] text-xs mt-1">
                      {form.errors.password}
                    </p>
                  )}
                </div>

                <div className="w-full sm:w-3/4 flex justify-end items-center">
                  <div
                    className="flex items-center space-x-1 cursor-pointer"
                    onClick={handleForgotPassword}
                  >
                    <FontAwesomeIcon
                      color="#003366"
                      icon={faLock}
                      className="relative top-[1px]"
                    />
                    <p className="text-xs font-[500] text-[#003366]">
                      Forgot Password
                    </p>
                  </div>
                </div>
                <div className="w-full sm:w-3/4">
                  <button
                    type="submit"
                    className="bg-[#003366] font-[500] text-white w-full rounded-lg py-2"
                  >
                    Sign In
                  </button>
                </div>
              </div>
            </form>
          </div>

          <AuthMobileFooter />
        </div>
        </div>
      </div>

    </>
  );
};

export default Login;
